<template>
    <section>
        <div class="buyflow-panel space-y-6">
            <CheckoutCardBar :open="open" :status="status" heading="Personal Information" @edit="edit" @cancel-edit="cancelEdit" />
            <form
                v-if="open"
                class="block space-y-6"
                data-cy="form-checkout"
            >
                <div class="grid grid-cols-6 gap-4">
                    <div class="col-span-6 desktop:col-span-3">
                        <label class="buyflow__inputCount block" for="first">
                            First Name
                            <input
                                v-model.trim="formFields.firstName"
                                :class="{ 'border-salmon': $v.formFields.firstName.$error }"
                                @input="checkLength($event, 'first')"
                                @keydown.enter.prevent
                                @copy.prevent
                                @paste.prevent
                                data-1p-ignore
                                data-lpignore="true"
                                autocomplete="new-password"
                                :maxlength="this.formFields.maxlengthFirstName"
                                class="block w-full rounded-sm"
                                data-cy="form-checkout-first-name"
                                id="first"
                                type="text"
                            >
                            <span
                                v-if="$v.formFields.firstName.$error && !$v.formFields.firstName.required"
                                class="text-salmon text-sm"
                            >
                                <IconCircleExclamation class="inline mr-1" />
                                Please enter your first name
                            </span>
                        </label>
                    </div>
                    <div class="col-span-6 desktop:col-span-3">
                        <label class="buyflow__inputCount block" for="last">
                            Last Name
                            <input
                                v-model.trim="formFields.lastName"
                                :class="{ 'border-salmon': $v.formFields.lastName.$error }"
                                @input="checkLength($event, 'last')"
                                @keydown.enter.prevent
                                @copy.prevent
                                @paste.prevent
                                data-1p-ignore
                                data-lpignore="true"
                                autocomplete="new-password"
                                :maxlength="this.formFields.maxlengthLastName"
                                class="block w-full rounded-sm"
                                data-cy="form-checkout-last-name"
                                id="last"
                                type="text"
                            >
                            <span
                                v-if="$v.formFields.lastName.$error && !$v.formFields.lastName.required"
                                class="text-salmon text-sm"
                            >
                                <IconCircleExclamation class="inline mr-1" />
                                Please enter your last name
                            </span>
                        </label>
                    </div>

                    <div class="col-span-6">
                        <div
                            class="flex row-reverse p-2 -mt-4 items-start"
                            :class="[{ 'bg-gray-100 border border-gray-200 border-t-0': this.warningReached }, { 'border-salmon': this.maximumExceeded }]"
                        >
                            <div
                                v-if="warningReached"
                                class="text-sm mr-4 text-gray-700"
                            >
                                <IconCircleExclamation
                                    v-if="maximumExceeded"
                                    class="inline mr-1"
                                />
                                Please note: <span class="font-bold">First Name</span> and <span
                                class="font-bold">Last Name</span> cannot exceed 24 characters total (incl. spaces/hypens).
                                If you must remove characters, remove them from <span class="font-bold">First Name</span> in
                                order to have a more complete <span class="font-bold">Last Name</span>.
                            </div>
                            <div
                                class="ml-auto flex-shrink-0 text-sm xtext-gray-700"
                                :class="{ 'font-bold bg-salmon text-white p-1': this.maximumExceeded }"
                            >
                                {{ this.currentCharacterCount }} / {{ this.formFields.maxCharacters }}
                            </div>
                        </div>
                    </div>

                    <div class="col-span-6 desktop:col-span-2">
                        <label>
                            Phone Number
                            <the-mask
                                v-model.trim="formFields.phoneNumber"
                                :class="{ 'border-salmon': $v.formFields.phoneNumber.$error }"
                                @keydown.enter.prevent
                                mask="###-###-####"
                                type="tel"
                                autocomplete="on"
                                class="block w-full rounded-sm"
                                data-cy="form-checkout-phone"
                            >
                            </the-mask>
                            <span v-if="$v.formFields.phoneNumber.$error && !$v.formFields.phoneNumber.required"
                                  class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Please enter a phone number
                            </span>
                            <span
                                v-else-if="$v.formFields.phoneNumber.$error && (!$v.formFields.phoneNumber.minLength || !$v.formFields.phoneNumber.maxLength)"
                                class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Please enter a valid phone number
                            </span>
                        </label>
                    </div>
                    <div class="col-span-6 desktop:col-span-2">
                        <label>
                            Email
                            <input
                                v-model.trim="formFields.emailAddress"
                                :class="{ 'border-salmon': $v.formFields.emailAddress.$error }"
                                type="text"
                                autocapitalize="none"
                                class="block w-full rounded-sm"
                                data-1p-ignore
                                @keydown.enter.prevent
                                data-cy="form-checkout-email-1"
                            >
                            <span v-if="isWywSelected"
                                  class="text-sm">
                                  This email may be used as your <strong>WiFi Your Way&trade; Home App</strong> username
                            </span>
                            <span v-if="$v.formFields.emailAddress.$error && !$v.formFields.emailAddress.required"
                                  class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Please enter an email address
                            </span>
                            <span v-else-if="$v.formFields.emailAddress.$error && !$v.formFields.emailAddress.email"
                                  class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Please enter a valid email address
                            </span>
                        </label>
                    </div>
                    <div class="col-span-6 desktop:col-span-2">
                        <label>
                            Confirm Email
                            <input
                                v-model.trim="formFields.confirmEmailAddress"
                                :class="{ 'border-salmon': $v.formFields.confirmEmailAddress.$error }"
                                type="text"
                                autocapitalize="none"
                                class="block w-full rounded-sm"
                                data-1p-ignore
                                @keydown.enter.prevent
                                data-cy="form-checkout-email-2"
                            >
                            <span
                                v-if="$v.formFields.confirmEmailAddress.$error && !$v.formFields.confirmEmailAddress.required"
                                class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Please re-enter your email address
                            </span>
                            <span v-else-if="$v.formFields.emailAddress.$error && !$v.formFields.confirmEmailAddress.email"
                                  class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Please enter a matching valid email address
                            </span>
                            <span
                                v-else-if="$v.formFields.confirmEmailAddress.$error && !$v.formFields.confirmEmailAddress.sameAsEmailAddress"
                                class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Email address does not match
                            </span>
                        </label>
                    </div>
                    <div class="col-span-6 desktop:col-span-2">
                        <label>
                            Referral Code
                            <AppModal>
                                <template #trigger>
                                    <IconCircleInfo class="inline cursor-pointer text-sm -mt-1"/>
                                </template>
                                <template #contents>
                                    <div class="text-sm font-normal break-words">
                                        Were you referred by a friend and received a referral code? Enter it&nbsp;here.
                                    </div>
                                </template>
                            </AppModal>
                            <input
                                v-model.trim="formFields.referralCode"
                                :class="{ 'border-salmon': $v.formFields.referralCode.$error || referralCodeError }"
                                @focus="referralCodeError = ''"
                                type="text"
                                name="referralCode"
                                autocapitalize="none"
                                class="block w-full rounded-sm"
                                maxlength="9"
                                data-1p-ignore
                                @keydown.enter.prevent
                                data-cy="form-referral-code">
                            <span
                                v-if="$v.formFields.referralCode.$error && !$v.formFields.referralCode.disallowedCharacters"
                                class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                {{failReferralCodeEvent()}}
                                Special characters are not allowed.
                            </span>
                            <span
                                v-else-if="$v.formFields.referralCode.$error && (!$v.formFields.referralCode.minLength || !$v.formFields.referralCode.maxLength)"
                                class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                {{failReferralCodeEvent()}}
                                The referral code must be 9 characters long.
                            </span>
                            <span v-else-if="$v.formFields.referralCode.$error"
                                  class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                {{failReferralCodeEvent()}}
                                The referral code is invalid. Please check and try again.
                            </span>
                            <span v-else-if="referralCodeError"
                                  class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                {{failReferralCodeEvent()}}
                                {{ referralCodeError }}
                            </span>
                        </label>
                    </div>
                    <div class="col-span-6">
                        <label class="flex items-center">
                            <input v-model.trim="formFields.sameAddress"
                                   type="checkbox"
                                   class="rounded-sm"
                                   @click="clearBillingAddress">
                            <span class="ml-1">My billing address is the same</span>
                        </label>
                    </div>
                </div>

                <div v-if="!formFields.sameAddress"
                     class="grid
                            grid-cols-6
                            gap-4">
                    <div class="col-span-6 tablet:col-span-4">
                        <label>
                            Street Address
                            <input ref="street-address"
                                   v-model.trim="formFields.line1"
                                   type="text"
                                   tabindex="1"
                                   maxlength="35"
                                   :class="{ 'border-salmon': $v.formFields.line1.$error }"
                                   class="block
                              w-full"
                                   @keydown.enter.prevent>
                            <span v-if="$v.formFields.line1.$error && !$v.formFields.line1.required"
                                  class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Please enter a valid Street Address
                            </span>
                            <span v-if="$v.formFields.line1.$error && !$v.formFields.line1.maxLength"
                                  class="text-salmon
                                     text-xs
                                     tablet:text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Street Address must be less than 35 characters
                            </span>
                        </label>
                    </div>
                    <div class="col-span-6 tablet:col-span-2">
                        <label>
                            Apt/Unit #
                            <input v-model.trim="formFields.line2"
                                   type="text"
                                   tabindex="2"
                                   maxlength="30"
                                   class="block
                                          w-full"
                                   @keydown.enter.prevent>
                            <span v-if="$v.formFields.line2.$error && !$v.formFields.line2.maxLength"
                                  class="text-salmon
                                     text-xs
                                     tablet:text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Apt/Unit # must be less than 30 characters
                            </span>
                        </label>
                    </div>
                    <div class="col-span-6 tablet:col-span-3">
                        <label>
                            State
                            <select v-model="formFields.stateOrProvince"
                                    tabindex="3"
                                    :class="{ 'border-salmon': $v.formFields.stateOrProvince.$error }"
                                    class="block
                               w-full">
                                <option selected
                                        disabled>
                                    Please select a state..
                                </option>
                                <option v-for="state in states"
                                        :key="state.abbr"
                                        :value="state.abbr">
                                    {{ state.name }}
                                </option>
                            </select>
                            <span v-if="$v.formFields.stateOrProvince.$error"
                                  class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Please select a State
                            </span>
                        </label>
                    </div>
                    <div class="col-span-6 tablet:col-span-3">
                        <label>
                            ZIP Code
                            <input v-model.trim="formFields.postalCode"
                                   type="text"
                                   maxlength="5"
                                   tabindex="4"
                                   :class="{ 'border-salmon': $v.formFields.postalCode.$error }"
                                   class="block
                              w-full"
                                   @keydown.enter.prevent>
                            <span v-if="$v.formFields.postalCode.$error"
                                  class="text-salmon text-sm">
                                <IconCircleExclamation class="inline mr-1"/>
                                Please enter a valid ZIP Code
                            </span>
                        </label>
                    </div>
                </div>

                <button
                    type="button"
                    class="track buyflow-button w-full desktop:w-auto flex items-center text-sm widescreen:text-base"
                    data-category="resi: checkout"
                    data-action="resi: checkout confirmation - submit information form"
                    data-label="resi: checkout confirmation page"
                    @click.prevent="submit()"
                    data-cy="form-checkout-schedule"
                >
                    <IconCircleNotch class="animate-spin text-white mr-2 text-lg" v-if="isLoading" />
                    {{ buttonText }}
                </button>
            </form>
        </div>
    </section>
</template>

<script>
import CONSTANTS from '^/constants'
import {TheMask,} from 'vue-the-mask'
import {email, helpers, maxLength, minLength, numeric, required, requiredUnless, sameAs} from "vuelidate/lib/validators"
import AppModal from "~/Buyflow/components/AppModal"
import IconCheck from "@/atoms/icons/IconCheck"
import IconCircleExclamation from "@/atoms/icons/IconCircleExclamation"
import IconCircleNotch from "@/atoms/icons/IconCircleNotch"
import CheckoutCardBar from "../CheckoutCardBar/index.vue";
import IconCircleInfo from "@/atoms/icons/IconCircleInfo"
import {captureException} from "@sentry/browser";
import {mapState} from "vuex";

const disallowedCharacters = helpers.regex('disallowedCharacters', /^[^\[\]_,'"|&@)\s\\]+$/)

export default {
    name: 'CheckoutForm',
    components: {
        CheckoutCardBar,
        TheMask,
        AppModal,
        IconCheck,
        IconCircleExclamation,
        IconCircleNotch,
        IconCircleInfo,
    },
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        status: {
            type: String,
            default: 'uncompleted',
        },
        isSelfInstallEligible: {
            type: Boolean,
            default: false,
        },
        isWywSelected: {
            type: Boolean,
            default: false,
        },
        firstName: {
            type: String,
            default: '',
        },
        lastName: {
            type: String,
            default: '',
        },
        phoneNumber: {
            type: String,
            default: '',
        },
        emailAddress: {
            type: String,
            default: '',
        },
        billingLine1: {
            type: String,
            default: '',
        },
        billingLine2: {
            type: String,
            default: '',
        },
        billingCity: {
            type: String,
            default: '',
        },
        billingStateOrProvince: {
            type: String,
            default: '',
        },
        billingPostalCode: {
            type: String,
            default: '',
        },
        referralCode: {
            type: String,
            default: '',
        },
        submitCustomerInformation: {
            type: Function,
            default: () => {
            },
        },
    },
    data: () => ({
        isLoading: false,
        formFields: {
            firstName: '',
            lastName: '',
            nameGroup: '',
            firstNameCount: 0,
            lastNameCount: 0,
            maxCharacters: 24,
            maxlengthFirstName: '',
            maxlengthLastName: '',
            phoneNumber: '',
            emailAddress: '',
            confirmEmailAddress: '',
            line1: '',
            line2: '',
            city: '',
            stateOrProvince: '',
            postalCode: '',
            sameAddress: true,
            referralCode: '',
        },
        referralCodeError: '',
        states: CONSTANTS.US_STATES,
    }),
    computed: {
        ...mapState(['meta']),
        currentCharacterCount() {
            return this.formFields.firstName.length + this.formFields.lastName.length
        },
        warningReached() {
            return this.currentCharacterCount >= 20
        },
        maximumReached() {
            return this.currentCharacterCount >= 24
        },
        maximumExceeded() {
            return this.currentCharacterCount > 24
        },
        buttonText() {
            return this.isSelfInstallEligible ? 'Check Installation Options' : 'Check Schedule/Delivery Times'
        },
    },
    validations: {
        formFields: {
            firstName: {
                required,
            },
            lastName: {
                required,
            },
            phoneNumber: {
                required,
                minLength: minLength(10),
                maxLength: maxLength(10),
            },
            emailAddress: {
                required,
                email,
            },
            confirmEmailAddress: {
                required,
                sameAsEmailAddress: sameAs('emailAddress'),
                email,
            },
            line1: {
                requiredUnless: requiredUnless('sameAddress'),
                maxLength: maxLength(35),
            },
            line2: {
                maxLength: maxLength(30),
            },
            stateOrProvince: {
                requiredUnless: requiredUnless('sameAddress'),
                minLength: minLength(2),
                maxLength: maxLength(2),
            },
            postalCode: {
                requiredUnless: requiredUnless('sameAddress'),
                numeric,
                minLength: minLength(5),
                maxLength: maxLength(5),
            },
            referralCode: {
                disallowedCharacters,
                minLength: minLength(9),
                maxLength: maxLength(9),
            },
        },
    },
    mounted() {
        this.formFields.firstName = this.firstName || ''
        this.formFields.firstNameCount = this.formFields.firstName?.length || 0
        this.formFields.lastName = this.lastName || ''
        this.formFields.lastNameCount = this.formFields.lastName?.length || 0
        this.formFields.phoneNumber = this.phoneNumber || ''
        this.formFields.emailAddress = this.emailAddress || ''
        this.formFields.line1 = this.billingLine1 || ''
        this.formFields.line2 = this.billingLine2 || ''
        this.formFields.city = this.billingCity || ''
        this.formFields.stateOrProvince = this.billingStateOrProvince || ''
        this.formFields.postalCode = this.billingPostalCode || ''
        this.formFields.referralCode = this.referralCode || ''
    },
    methods: {
        clearBillingAddress() {
            this.formFields.line1 = ''
            this.formFields.line2 = ''
            this.formFields.stateOrProvince = ''
            this.formFields.postalCode = ''
        },
        checkLength(event, field) {
            const length = event.target.value.length

            switch (field) {
                case 'first':
                    this.formFields.firstNameCount = length
                    break
                case 'last':
                    this.formFields.lastNameCount = length
                    break
            }

            if (this.maximumReached) {
                this.formFields.maxlengthFirstName = this.formFields.firstNameCount
                this.formFields.maxlengthLastName = this.formFields.lastNameCount
            } else {
                this.formFields.maxlengthFirstName = ''
                this.formFields.maxlengthLastName = ''
            }
        },
        edit() {
            this.$emit('edit-customer-information')
            this.$emit('set-finalize-state', {
                open: 'form',
            })
        },
        cancelEdit(){
            this.$emit('set-finalize-state', {
                open: this.isSelfInstallEligible? 'selfInstall': 'scheduler',
            })
        },
        hotJarStateChange() {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
                event: 'hotJarManualStateChange',
                hotJarPath: 'checkout/finalize/checkSchedule'
            })
        },
        submit() {
            if(this.isLoading) return // prevent double click
            this.isLoading = true
            this.$v.$touch()

            // Fire HotJar Event
            this.hotJarStateChange()

            if (this.$v.$invalid || this.maximumExceeded) {
                // highlight the fields with errors and do not emit anything
                this.isLoading = false
            } else {
                this.$emit('clear-schedule')
                this.submitCustomerInformation(this.formFields)
                    .then(response => {
                        if (this.formFields.referralCode) {
                            this.sendEvent('checkout', 'finalize', 'referral code success')
                        }

                        //Set form meta
                        this.$store.commit('SET_METADATA', {
                            firstName: this.formFields.firstName,
                            lastName: this.formFields.lastName,
                            phoneNumber: this.formFields.phoneNumber,
                            emailAddress: this.formFields.emailAddress,
                        })
                           
                        setTimeout(() => {
                            this.$emit('set-finalize-state', {
                                open: this.isSelfInstallEligible? 'selfInstall': 'scheduler',
                                form: 'completed',
                                scheduler: 'uncompleted',
                            })
                            this.isLoading = false
                        }, 500)
                    })
                    .catch(e => {
                        this.isLoading = false
                        if (e.response?.data?.errors?.referralCode?.length) {
                            this.$store.commit('SET_METADATA', {
                                referralCodeUsed: false,
                            }, ROOT)
                            this.referralCodeError = `The referral code ${this.formFields.referralCode} is invalid.`
                            this.formFields.referralCode = ''
                        } else {
                            captureException(e)

                            this.$store.dispatch('togglePopup', {
                                type: 'COMMON/ERRORS/UNEXPECTED',
                                error: e,
                            }, ROOT)
                        }
                    })
            }
        },
        failReferralCodeEvent() {
            this.sendEvent('checkout', 'finalize', 'referral code failure')
        },
    },
}
</script>
